import { QueryResult, useQuery } from '@apollo/client';
import { ErrorMessage, SearchUiMessageResult, UiMessage, UiMessageKey } from '@bechtel/sr-types';
import { find, forEach, get, has, replace } from 'lodash';
import { useLanguage } from '~/components/molecules';
import { SEARCH_UI_MESSAGE } from '~/gql';

export enum MessageType {
  ERROR,
  INFO,
  QUESTION,
}

export interface Message {
  messageText: string;
  buttons: string[];
  messageType: MessageType;
  messageKey: UiMessageKey | string;
}

export interface UseMessageResult {
  messages: Message[];
  queryResult: QueryResult<{ searchUiMessages: SearchUiMessageResult }, Record<string, any>>;
}

export const getButtons = (messageButtons: string) => {
  switch (messageButtons) {
    case 'YES_NO':
      return ['Yes', 'No'];
    case 'YES_NO_CANCEL':
      return ['Yes', 'No', 'Cancel'];
    default:
      return [];
  }
};

export const getMessageType = (messageType: string) => {
  switch (messageType) {
    case 'STOP':
    case 'EXCLAMATION':
      return MessageType.ERROR;
    case 'QUESTION':
      return MessageType.QUESTION;
    case 'INFORMATION':
      return MessageType.INFO;
    default:
      return MessageType.INFO;
  }
};

const getLanguageMessages = (
  messageKeys: ErrorMessage[] | UiMessageKey[],
  messageList: UiMessage[],
  isAlternateLanguageUsed: boolean
) => {
  const messages: Message[] = [];
  if (!messageKeys || !messageList) messages;

  forEach(messageKeys, (msg: ErrorMessage | UiMessageKey) => {
    let key = '';

    if (has(msg, '__typename')) {
      if (msg['__typename'] === 'ErrorKey') {
        key = msg['key'];
      } else if (msg['__typename'] === 'ServerMessage') {
        key = msg['messageKey'];
      }
    } else {
      key = msg;
    }

    const foundMessage = find(messageList, (message: UiMessage) => message.messageKey === key);

    if (foundMessage) {
      const message: Message = {
        messageText: replace(
          isAlternateLanguageUsed
            ? msg && has(msg, 'alternateMessage')
              ? get(msg, 'alternateMessage', '')
              : foundMessage.alternateMessage
            : msg && has(msg, 'message')
            ? get(msg, 'message', '')
            : foundMessage.message,
          /\\n/g,
          '\n'
        ),
        buttons: getButtons(foundMessage.messageButtons),
        messageType: getMessageType(foundMessage.messageType),
        messageKey: foundMessage.messageKey as UiMessageKey,
      };
      messages.push(message);
    }
  });
  return messages;
};

export function useMessage(messages: ErrorMessage[] | UiMessageKey[]): UseMessageResult {
  const { isAlternateLanguageUsed } = useLanguage();
  const response = useQuery<{ searchUiMessages: SearchUiMessageResult }>(SEARCH_UI_MESSAGE, {
    variables: {
      filterInput: { and: null },
      paginationInput: {
        limit: 500,
      },
    },
  });

  const languageMessages = getLanguageMessages(
    messages,
    get(response.data, 'searchUiMessages.items', []),
    isAlternateLanguageUsed
  );

  return { messages: languageMessages, queryResult: response };
}
